import { createGlobalStyle } from "styled-components";
import { extendTheme } from "@mui/joy";
import type { SxProps } from "@mui/joy/styles/types";
import { getZekayThemeRootCssText, ZEKAY_THEME_PRESETS } from "./themePresets";

const zekayRootCssDefaults = getZekayThemeRootCssText(ZEKAY_THEME_PRESETS.ocean_blue);

export const colorTokens = {
  bg: {
    app: "var(--zekay-bg-app)",
    surface: "var(--zekay-bg-surface)",
    surfaceElevated: "var(--zekay-bg-surface-elevated)",
    surfaceMuted: "var(--zekay-bg-surface-muted)",
    hover: "var(--zekay-bg-hover)",
    overlay: "#000000",
    sheetLight: "#FFFFFF",
  },
  text: {
    primary: "#FFFFFF",
    secondary: "#E0E0E0",
    muted: "#A8A8A8",
    inverse: "#111111",
    dimmed: "#888888",
    faint: "#AAAAAA",
    soft: "#BBBBBB",
    subtle: "#999999",
    onLightSecondary: "#4A4A4A",
    onLightMuted: "#6B6B6B",
    placeholder: "#9E9E9E",
    strongInverse: "#000000",
    title: "#FCCB06",
    legend: "#FCCB06",
  },
  border: {
    default: "var(--zekay-border-default)",
    strong: "var(--zekay-border-strong)",
    focus: "var(--zekay-border-focus)",
  },
  accent: {
    primary: "var(--zekay-accent-primary)",
    hover: "var(--zekay-accent-hover)",
    contrastText: "var(--zekay-accent-contrast-text)",
  },
  status: {
    success: "#008000",
    successHover: "#006600",
    warning: "#F3B35A",
    danger: "#E86A6A",
    dangerHover: "#D45A5A",
    info: "#78B8FF",
  },
} as const;

export const codeFontFamily =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

export const emailBodyFontFamily = "Arial, Helvetica, sans-serif";

export const emojiFontFamily =
  'ui-sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", sans-serif';

export const textTokens = {
  fontFamily: '"Public Sans", sans-serif',
  color: {
    primary: colorTokens.text.primary,
    secondary: colorTokens.text.secondary,
    muted: colorTokens.text.muted,
    inverse: colorTokens.text.inverse,
    dimmed: colorTokens.text.dimmed,
    faint: colorTokens.text.faint,
    soft: colorTokens.text.soft,
    subtle: colorTokens.text.subtle,
    onLightSecondary: colorTokens.text.onLightSecondary,
    onLightMuted: colorTokens.text.onLightMuted,
    placeholder: colorTokens.text.placeholder,
    strongInverse: colorTokens.text.strongInverse,
    title: colorTokens.text.title,
    legend: colorTokens.text.legend,
    bodyOnDark: colorTokens.text.primary,
    bodyOnLight: colorTokens.text.inverse,
  },
  size: {
    xs: "0.75rem",
    sm: "0.875rem",
    md: "1rem",
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
} as const;

export const trainingStatusGlyphSizes = {
  menuItem: { xs: "1.2rem", sm: "1.3rem" } as const,
  pendingRevertIcon: "1.05rem",
} as const;

export const trainingStatusMenuSx: SxProps = {
  zIndex: 10000,
  "--unstable_popup-zIndex": 10000,
  minWidth: 220,
  maxWidth: "95vw",
  py: 0.5,
  px: 0.5,
  fontFamily: textTokens.fontFamily,
  color: textTokens.color.primary,
  "--List-radius": "8px",
  bgcolor: `${colorTokens.bg.surfaceElevated} !important`,
  border: `1px solid ${colorTokens.border.strong}`,
  boxShadow: `0 8px 24px ${colorTokens.bg.overlay}66`,
  colorScheme: "dark",
  "& .MuiList-root": { py: 0.5 },
  "& [role='group']": { p: 0 },
};

export const trainingStatusMenuItemSx = (isDanger: boolean): SxProps => ({
  justifyContent: "flex-start",
  minHeight: 44,
  py: 0.5,
  px: 1.25,
  my: 0.125,
  borderRadius: "6px",
  fontFamily: textTokens.fontFamily,
  color: isDanger
    ? `${colorTokens.status.danger} !important`
    : `${textTokens.color.primary} !important`,
  bgcolor: "transparent",
  gap: 1,
  "&:hover": {
    backgroundColor: isDanger
      ? `${withAlpha(colorTokens.status.danger, 0.12)} !important`
      : `${colorTokens.bg.hover} !important`,
  },
  "&.Mui-focusVisible": {
    backgroundColor: isDanger
      ? `${withAlpha(colorTokens.status.danger, 0.14)} !important`
      : `${colorTokens.bg.hover} !important`,
  },
  "&:active": {
    backgroundColor: isDanger
      ? `${withAlpha(colorTokens.status.danger, 0.18)} !important`
      : `${withAlpha(colorTokens.bg.hover, 0.8)} !important`,
  },
});

export function withAlpha(color: string, alpha: number): string {
  const a = Math.max(0, Math.min(1, alpha));
  const t = color.trim();
  if (t.startsWith("var(")) {
    const pct = Math.round(a * 100);
    return `color-mix(in srgb, ${t} ${pct}%, transparent)`;
  }
  const normalized = t.replace("#", "");
  if (normalized.length !== 6) return `rgba(255,255,255,${a})`;

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${a})`;
}

export const GlobalStyles = createGlobalStyle`
  :root {
${zekayRootCssDefaults}
  }
  html,
  body {
    background-color: ${colorTokens.bg.app};
    color: ${textTokens.color.primary};
    font-family: ${textTokens.fontFamily};
    overflow: auto;
  }

  .MuiTypography-root,
  .MuiInputBase-input,
  .MuiButton-root,
  .MuiChip-label {
    color: inherit;
    font-family: inherit;
  }

  .MuiFormLabel-root {
    color: ${colorTokens.text.legend};
    font-family: ${textTokens.fontFamily};
  }

  html {
    font-size: 16px;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const successPaletteOverrides = {
  solidBg: colorTokens.status.success,
  solidHoverBg: colorTokens.status.successHover,
  solidActiveBg: colorTokens.status.successHover,
  solidColor: colorTokens.text.primary,
  solidDisabledBg: colorTokens.bg.surfaceMuted,
  solidDisabledColor: colorTokens.text.muted,
  softColor: colorTokens.status.success,
  softBg: withAlpha(colorTokens.status.success, 0.2),
  softHoverBg: withAlpha(colorTokens.status.success, 0.28),
  softActiveBg: withAlpha(colorTokens.status.success, 0.35),
  outlinedColor: colorTokens.status.success,
  outlinedBorder: colorTokens.status.success,
  outlinedHoverBg: withAlpha(colorTokens.status.success, 0.12),
  outlinedHoverBorder: colorTokens.status.successHover,
  outlinedActiveBg: withAlpha(colorTokens.status.success, 0.2),
  plainColor: colorTokens.status.success,
  plainHoverBg: withAlpha(colorTokens.status.success, 0.12),
  plainActiveBg: withAlpha(colorTokens.status.success, 0.2),
} as const;

/** Tooltip Popper renders in portal; must sit above modals, drawers, selects (10000+) and toasts (10100). */
const tooltipAboveModalZIndex = 10200;

export const zekayTheme = extendTheme({
  colorSchemes: {
    light: {
      palette: {
        background: {
          body: colorTokens.bg.app,
        },
        text: {
          primary: colorTokens.text.primary,
        },
        success: successPaletteOverrides,
      },
    },
    dark: {
      palette: {
        background: {
          body: colorTokens.bg.app,
        },
        text: {
          primary: colorTokens.text.primary,
        },
        success: successPaletteOverrides,
      },
    },
  },
  typography: {
    h1: {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    h2: {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    h3: {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    h4: {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
      fontSize: textTokens.size.md,
    },
    "title-lg": {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    "title-md": {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    "title-sm": {
      color: textTokens.color.title,
      fontFamily: textTokens.fontFamily,
    },
    "body-lg": {
      color: textTokens.color.bodyOnDark,
      fontFamily: textTokens.fontFamily,
    },
    "body-md": {
      color: textTokens.color.bodyOnDark,
      fontFamily: textTokens.fontFamily,
      fontSize: textTokens.size.md,
      fontWeight: textTokens.weight.regular,
    },
    "body-sm": {
      color: textTokens.color.secondary,
      fontFamily: textTokens.fontFamily,
      fontSize: textTokens.size.sm,
      fontWeight: textTokens.weight.regular,
    },
    "body-xs": {
      color: textTokens.color.muted,
      fontFamily: textTokens.fontFamily,
      fontSize: textTokens.size.xs,
    },
  },
  fontFamily: {
    body: textTokens.fontFamily,
  },
  zIndex: {
    modal: 1600,
    tooltip: tooltipAboveModalZIndex,
  },
  components: {
    JoyTooltip: {
      defaultProps: {
        variant: "soft",
        color: "neutral",
        // Root slot is Base Popper; theme typings expect full PopperProps (e.g. open).
        slotProps: {
          root: {
            sx: {
              zIndex: tooltipAboveModalZIndex,
            },
          },
        } as never,
      },
      styleOverrides: {
        root: ({ ownerState }) => {
          if (ownerState.variant !== "soft" || ownerState.color !== "neutral") {
            return {};
          }
          return {
            backgroundColor: colorTokens.bg.surfaceMuted,
            color: colorTokens.text.primary,
            border: `1px solid ${colorTokens.border.strong}`,
            boxShadow: `0 6px 22px ${withAlpha("#000000", 0.26)}`,
          };
        },
        arrow: ({ ownerState }) => {
          if (ownerState.variant !== "soft" || ownerState.color !== "neutral") {
            return {};
          }
          const bg = colorTokens.bg.surfaceMuted;
          const bc = colorTokens.border.strong;
          return {
            "&::before": {
              borderTopColor: bg,
              borderRightColor: bg,
              boxShadow: `var(--variant-borderWidth, 1px) calc(-1 * var(--variant-borderWidth, 1px)) 0 0 ${bc}`,
            },
          };
        },
      },
    },
  },
});

export const mailEditorShellSx: SxProps = {
  width: "100%",
  flex: 1,
  minHeight: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: colorTokens.bg.surface,
  overflow: "hidden",
};

/** Root Tabs in dark modals: overrides Joy default (paper) and contains overscroll tint */
export const zekayModalTabsRootSx: SxProps = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: colorTokens.bg.surface,
  overscrollBehavior: "contain",
  color: colorTokens.text.primary,
};

/** Horizontal TabList inside dark modals (theory test results, profile form, etc.) */
export const zekayModalTabListSx: SxProps = {
  flexShrink: 0,
  pt: 1,
  px: { xs: 1, sm: 2 },
  mb: 0,
  backgroundColor: colorTokens.bg.surface,
  borderBottom: "1px solid",
  borderColor: "divider",
  overflowX: "auto",
  overscrollBehaviorX: "contain",
  scrollbarColor: `${withAlpha(colorTokens.border.strong, 0.8)} ${colorTokens.bg.surface}`,
  "& .MuiTab-root": {
    color: withAlpha(colorTokens.text.primary, 0.6),
    minWidth: "auto",
    flexShrink: 0,
    whiteSpace: "nowrap",
    "&:hover": { color: colorTokens.accent.contrastText },
    "&.Mui-selected": {
      color: colorTokens.accent.contrastText,
      fontWeight: 600,
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        bottom: -1,
        left: 0,
        right: 0,
        height: 2,
        backgroundColor: colorTokens.accent.primary,
        borderRadius: "2px 2px 0 0",
      },
    },
  },
};

export const zekayModalTabPanelSx: SxProps = {
  p: { xs: 1, sm: 2 },
  overflow: "auto",
  flex: 1,
  minHeight: 0,
  backgroundColor: colorTokens.bg.surface,
  color: colorTokens.text.primary,
  overscrollBehavior: "contain",
  scrollbarColor: `${withAlpha(colorTokens.border.strong, 0.8)} ${colorTokens.bg.surface}`,
};

/** Identity column: avatar / photo, centered on xs, top-aligned from md (profile modals). */
export const profileFormIdentityPhotoColumnSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: { xs: "center", md: "flex-start" },
  minWidth: 0,
  width: "100%",
  maxWidth: "100%",
};

/** Joy FormLabel on dark profile forms (aligned with DarkTypography secondary + touch-friendly density). */
export const profileFormFieldLabelSx: SxProps = {
  color: colorTokens.text.secondary,
  fontSize: { xs: "0.75rem", sm: "0.8rem" },
  fontWeight: 500,
  mb: 0.5,
};

export const mailEditorPreviewSheetSx: SxProps = {
  p: 1,
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  backgroundColor: colorTokens.bg.surfaceElevated,
  borderColor: colorTokens.border.default,
};

export const mailEditorBlockCardSx: SxProps = {
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "8px",
  p: 1.25,
  mb: 1,
  backgroundColor: colorTokens.bg.surfaceElevated,
};

export const mailEditorEnvelopeStripSheetSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceElevated,
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "8px",
  p: { xs: 1, sm: 1.25 },
  display: "flex",
  flexDirection: "column",
  gap: 1,
  width: "100%",
  flexShrink: 0,
  boxSizing: "border-box",
  maxWidth: "100%",
};

export const mailEditorVariablesZoneSx: SxProps = {
  flexShrink: 0,
  minWidth: 0,
  width: "100%",
  p: { xs: 0.75, sm: 1 },
  borderRadius: "8px",
  border: `1px solid ${colorTokens.border.default}`,
  bgcolor: colorTokens.bg.surfaceMuted,
  display: "flex",
  flexDirection: "column",
  gap: 0.5,
  boxSizing: "border-box",
};

export const mailEditorBodyWorkspaceSx: SxProps = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
};

export const mailEditorOverflowSheetSx: SxProps = {
  position: "absolute",
  backgroundColor: colorTokens.bg.surfaceMuted,
  borderColor: colorTokens.border.default,
  zIndex: 1300,
  mt: 1,
  p: 1,
  boxShadow: "md",
  maxWidth: 360,
  maxHeight: 220,
  overflowY: "auto",
  pointerEvents: "auto",
};

export const hrSheetSx: SxProps = {
  backgroundColor: colorTokens.bg.surface,
  borderRadius: "8px",
  overflow: "hidden",
};

export const hrTableHeadSx: SxProps = {
  "& thead th": {
    backgroundColor: colorTokens.bg.surfaceElevated,
    color: colorTokens.text.title,
    fontWeight: "bold",
  },
};

export const hrTableBodySx: SxProps = {
  "& tbody tr": {
    backgroundColor: colorTokens.bg.surface,
    color: textTokens.color.primary,
    "&:hover": {
      backgroundColor: colorTokens.bg.surfaceElevated,
    },
  },
};

export const hrTableSx: SxProps = {
  ...hrTableHeadSx,
  ...hrTableBodySx,
};

export const hrCompactTableSx: SxProps = {
  ...hrTableSx,
  tableLayout: "fixed",
  "& thead th": {
    backgroundColor: colorTokens.bg.surfaceElevated,
    color: colorTokens.text.title,
    fontWeight: "bold",
    fontSize: "0.75rem",
    py: 1,
  },
  "& tbody td": {
    py: 1,
  },
  // Extra breathing room on the row edges so first/last cell never hug the surrounding Sheet/Box border.
  "& thead th:first-of-type, & tbody td:first-of-type": {
    pl: 2,
  },
  "& thead th:last-of-type, & tbody td:last-of-type": {
    pr: 2,
  },
};

export const hrFilterSelectSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: textTokens.color.primary,
  border: `1px solid ${colorTokens.border.default}`,
};

export const hrTooltipSurfaceSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  border: `1px solid ${colorTokens.border.strong}`,
};

export const hrCommentCountChipSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: textTokens.color.primary,
  px: 0.75,
  cursor: "pointer",
};

export const alertsDrawerItemGridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 2.5rem 8rem",
  columnGap: 1,
  rowGap: 0.5,
  alignItems: "center",
};

export const alertsDrawerCountSx: SxProps = {
  justifySelf: "center",
  textAlign: "center",
  fontWeight: 700,
};

export const alertsDrawerCategoryChipSx: SxProps = {
  justifySelf: "center",
  px: 1,
  py: 0.2,
  borderRadius: 999,
  fontWeight: 700,
  letterSpacing: "0.03em",
  flexShrink: 0,
  bgcolor: withAlpha(colorTokens.accent.primary, 0.15),
  border: `1px solid ${colorTokens.accent.primary}`,
  color: colorTokens.accent.primary,
  textTransform: "uppercase",
};

export const hrModalDialogSx: SxProps = {
  bgcolor: colorTokens.bg.surface,
  border: `1px solid ${colorTokens.border.default}`,
};

export const hrCalloutWarningSx: SxProps = {
  p: { xs: 0.75, sm: 1 },
  backgroundColor: withAlpha(colorTokens.status.danger, 0.12),
  borderRadius: "6px",
  border: `1px solid ${withAlpha(colorTokens.status.danger, 0.4)}`,
};

export const hrPanelMutedSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  borderRadius: "6px",
};

export const hrMobileRequestCardSx: SxProps = {
  mb: 1,
  backgroundColor: colorTokens.bg.surfaceElevated,
  border: `1px solid ${colorTokens.border.default}`,
  p: { xs: 1, sm: 1.5 },
};

/** Excursion booking (`ExcursionBookingPage`): `IconButtonCTA` sizing for table + dense rows. */
export const excursionBookingCompactCtaSx: SxProps = {
  "--IconButton-size": "22px",
  flexShrink: 0,
};

/** Mobile booking card: sole delete action slightly larger for touch. */
export const excursionBookingMobileDeleteCtaSx: SxProps = {
  "--IconButton-size": "32px",
  flexShrink: 0,
};

// ─── Equipments (`EquipmentCards` attribute grid) ──────────────────────────────

/** Row height follows the tallest cell; controls sit on one horizontal band under labels. */
export const equipmentCardsAttributeGridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    md: "repeat(3, 1fr)",
    lg: "repeat(auto-fit, minmax(150px, 1fr))",
  },
  gap: 0.75,
  alignItems: "stretch",
};

export const equipmentCardsAttributeCellSx: SxProps = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
};

export const equipmentCardsAttributeLabelSx: SxProps = {
  fontWeight: 600,
  mb: 0.3,
  lineHeight: 1.25,
  letterSpacing: "0.3px",
  flexShrink: 0,
};

export const equipmentCardsEditControlSlotSx: SxProps = {
  width: "100%",
  minWidth: 0,
  flexShrink: 0,
};

export const hrIconButtonSuccessSx: SxProps = {
  backgroundColor: colorTokens.status.success,
  "&:hover": { backgroundColor: colorTokens.status.successHover },
};

export const hrIconButtonWarningSx: SxProps = {
  backgroundColor: colorTokens.status.warning,
  "&:hover": { backgroundColor: withAlpha(colorTokens.status.warning, 0.85) },
};

export const hrIconButtonDangerSoftSx: SxProps = {
  backgroundColor: colorTokens.status.danger,
  "&:hover": { backgroundColor: colorTokens.status.dangerHover },
};

export const hrCardSx: SxProps = {
  backgroundColor: colorTokens.bg.surface,
  color: colorTokens.text.primary,
};

export const hrAccentColor = colorTokens.text.title;

export const darkBorder = `1px solid ${colorTokens.border.default}`;

export const ctaAccentColor = colorTokens.text.title;
export const ctaAccentHoverColor = "#E5B800";

export const hrTextareaChromeSx: SxProps = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: textTokens.color.primary,
  border: `1px solid ${colorTokens.border.strong}`,
  borderRadius: "8px",
  "&:hover": { borderColor: ctaAccentColor },
  "&:focus-within": { borderColor: ctaAccentColor },
  "& textarea": { color: textTokens.color.primary },
  "& textarea::placeholder": { color: textTokens.color.placeholder, opacity: 1 },
  "& a": {
    color: "inherit",
    WebkitTextFillColor: "inherit",
    textDecoration: "underline",
    textDecorationColor: "currentColor",
  },
};

export const mailEditorTextareaSx: SxProps = [
  hrTextareaChromeSx,
  {
    minHeight: 72,
    fontSize: textTokens.size.sm,
    width: "100%",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
];

export const ctaAccentTextColor = "#1A1A1A";
export const ctaSuccessColor = colorTokens.status.success;
export const ctaSuccessHoverColor = colorTokens.status.successHover;
export const ctaSuccessLightColor = "#8FDFC8";
export const ctaSuccessLightHoverColor = "#5FD4A8";
export const ctaDangerColor = colorTokens.status.danger;
export const ctaDangerHoverColor = colorTokens.status.dangerHover;

const ctaDisabledGray = colorTokens.text.muted;

export const ctaIconButtonDisabledSx: SxProps = {
  "&:disabled, &[aria-disabled='true'], &.Mui-disabled": {
    color: `${ctaDisabledGray} !important`,
    "--Icon-color": `${ctaDisabledGray} !important`,
    backgroundColor: "transparent !important",
    opacity: 1,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "transparent !important",
      color: `${ctaDisabledGray} !important`,
      "--Icon-color": `${ctaDisabledGray} !important`,
    },
    "& .MuiSvgIcon-root": {
      color: `${ctaDisabledGray} !important`,
    },
  },
  "&:disabled:hover, &[aria-disabled='true']:hover, &.Mui-disabled:hover": {
    color: `${ctaDisabledGray} !important`,
    "--Icon-color": `${ctaDisabledGray} !important`,
    backgroundColor: "transparent !important",
    "& .MuiSvgIcon-root": {
      color: `${ctaDisabledGray} !important`,
    },
  },
};

/** Shared flex layout for IconButtonCTA wrappers. */
export const iconButtonCtaBaseSx: SxProps = { flexShrink: 0 };

export const ctaAccentEnabledSx: SxProps = {
  color: `${ctaAccentColor} !important`,
  "--Icon-color": `${ctaAccentColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaAccentColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaAccentColor, 0.15),
    color: `${ctaAccentHoverColor} !important`,
    "--Icon-color": `${ctaAccentHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaAccentHoverColor} !important`,
    },
  },
};

export const ctaSuccessEnabledSx: SxProps = {
  color: `${ctaSuccessColor} !important`,
  "--Icon-color": `${ctaSuccessColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaSuccessColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaSuccessColor, 0.2),
    color: `${ctaSuccessHoverColor} !important`,
    "--Icon-color": `${ctaSuccessHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaSuccessHoverColor} !important`,
    },
  },
};

export const ctaSuccessLightEnabledSx: SxProps = {
  color: `${ctaSuccessLightColor} !important`,
  "--Icon-color": `${ctaSuccessLightColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaSuccessLightColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaSuccessLightColor, 0.22),
    color: `${ctaSuccessLightHoverColor} !important`,
    "--Icon-color": `${ctaSuccessLightHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaSuccessLightHoverColor} !important`,
    },
  },
};

export const ctaDangerEnabledSx: SxProps = {
  color: `${ctaDangerColor} !important`,
  "--Icon-color": `${ctaDangerColor} !important`,
  backgroundColor: "transparent",
  "& .MuiSvgIcon-root": {
    color: `${ctaDangerColor} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: withAlpha(ctaDangerColor, 0.2),
    color: `${ctaDangerHoverColor} !important`,
    "--Icon-color": `${ctaDangerHoverColor} !important`,
    "& .MuiSvgIcon-root": {
      color: `${ctaDangerHoverColor} !important`,
    },
  },
};

export const ctaAccentSx: SxProps = [ctaAccentEnabledSx, ctaIconButtonDisabledSx];
export const ctaSuccessSx: SxProps = [ctaSuccessEnabledSx, ctaIconButtonDisabledSx];
export const ctaDangerSx: SxProps = [ctaDangerEnabledSx, ctaIconButtonDisabledSx];

export type CtaVariant = "accent" | "success" | "danger" | "neutral";

export type JoyIconButtonSemanticColor = "danger" | "success" | "neutral";

export const joyIconButtonColorByCtaVariant: Record<CtaVariant, JoyIconButtonSemanticColor> = {
  danger: "danger",
  success: "success",
  accent: "neutral",
  neutral: "neutral",
};

export const ctaVariantEnabledSxMap: Record<CtaVariant, SxProps> = {
  accent: ctaAccentEnabledSx,
  success: ctaSuccessEnabledSx,
  danger: ctaDangerEnabledSx,
  neutral: ctaAccentEnabledSx,
};

export const ctaVariantSxMap: Record<CtaVariant, SxProps> = {
  accent: ctaAccentSx,
  success: ctaSuccessSx,
  danger: ctaDangerSx,
  neutral: ctaAccentSx,
};

export const excursionCardGlassBorder = `1px solid ${withAlpha(colorTokens.text.primary, 0.2)}`;
export const excursionCardGlassBorderStrong = `1px solid ${withAlpha(colorTokens.text.primary, 0.25)}`;
export const excursionPlanningProjectionHighlightSx = {
  border: `2px dashed ${withAlpha(ctaAccentColor, 0.8)}`,
  backgroundColor: withAlpha(ctaAccentColor, 0.15),
};
export const excursionResizeHandleHoverSx = {
  "&:hover": { backgroundColor: withAlpha(colorTokens.text.primary, 0.15) },
};
export const excursionTooltipCardInteractiveHoverSx = {
  cursor: "pointer" as const,
  "&:hover": { borderColor: withAlpha(ctaAccentColor, 0.6) },
};
export const excursionTooltipParamLabelColor = withAlpha(colorTokens.text.primary, 0.7);
export const excursionTooltipParamLabelColorSoft = withAlpha(colorTokens.text.primary, 0.65);
export const excursionTooltipMutedTextColor = withAlpha(colorTokens.text.primary, 0.6);
export const excursionTooltipMutedTextSoftColor = withAlpha(colorTokens.text.primary, 0.5);
export const excursionTooltipSublineColor = withAlpha(colorTokens.text.primary, 0.85);
export const excursionPersonChipOnGradientSx = {
  backgroundColor: withAlpha(colorTokens.text.primary, 0.1),
  color: textTokens.color.primary,
};
export const excursionDropTargetEmptySx: SxProps = {
  backgroundColor: withAlpha(colorTokens.bg.overlay, 0.15),
  borderRadius: 6,
  p: 0.75,
  minHeight: 44,
  border: `1px dashed ${withAlpha(colorTokens.text.primary, 0.35)}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": { borderColor: withAlpha(colorTokens.text.primary, 0.45) },
  transition: "border-color 0.15s ease",
};
export const excursionClientChipDraggableSx = {
  backgroundColor: colorTokens.bg.surfaceMuted,
  color: textTokens.color.primary,
  cursor: "grab" as const,
  "&:active": { cursor: "grabbing" as const },
};
export const excursionClientChipReadonlySx = {
  backgroundColor: "transparent",
  color: withAlpha(colorTokens.text.primary, 0.9),
};
export const excursionGroupHeaderDividerBorder = `1px solid ${withAlpha(colorTokens.text.primary, 0.15)}`;

/** PersonChip / plain IconButton: comment bubble when there are no comments yet (avoids accent hover). */
export const excursionCommentIconEmptyPlainSx: SxProps = {
  color: `${colorTokens.text.muted} !important`,
  "--Icon-color": `${colorTokens.text.muted} !important`,
  backgroundColor: "transparent !important",
  "& .MuiSvgIcon-root": {
    color: `${colorTokens.text.muted} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: `${withAlpha(colorTokens.text.muted, 0.12)} !important`,
    color: `${colorTokens.text.muted} !important`,
    "--Icon-color": `${colorTokens.text.muted} !important`,
    "& .MuiSvgIcon-root": {
      color: `${colorTokens.text.muted} !important`,
    },
  },
};

/** Excursion group header: soft comment button when the group has no comments. */
export const excursionGroupCommentBubbleEmptySoftSx: SxProps = {
  color: `${colorTokens.text.muted} !important`,
  "--Icon-color": `${colorTokens.text.muted} !important`,
  backgroundColor: `${withAlpha(colorTokens.text.muted, 0.12)} !important`,
  boxShadow: "none",
  "& .MuiSvgIcon-root": {
    color: `${colorTokens.text.muted} !important`,
  },
  "&:hover:not(:disabled):not([aria-disabled='true']):not(.Mui-disabled)": {
    backgroundColor: `${withAlpha(colorTokens.text.muted, 0.2)} !important`,
    color: `${colorTokens.text.muted} !important`,
    "--Icon-color": `${colorTokens.text.muted} !important`,
    "& .MuiSvgIcon-root": {
      color: `${colorTokens.text.muted} !important`,
    },
  },
};

export const menuTileCardSx: SxProps = {
  width: "100%",
  maxWidth: "100%",
  minHeight: { xs: 56, sm: 64 },
  minWidth: 0,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: { xs: 1.5, sm: 2 },
  py: { xs: 1.25, sm: 1.5 },
  px: { xs: 1.5, sm: 2 },
  position: "relative",
  cursor: "pointer",
  borderRadius: "14px",
  background: `linear-gradient(180deg, ${withAlpha(colorTokens.text.primary, 0.055)}, ${withAlpha(colorTokens.text.primary, 0.025)})`,
  border: `1px solid ${withAlpha(colorTokens.text.primary, 0.08)}`,
  backdropFilter: "blur(8px)",
  overflow: "hidden",
  transition: "transform .14s ease, box-shadow .14s ease, border-color .14s ease",
  outline: "1px solid transparent",
  boxShadow: `0 1px 0 ${withAlpha(colorTokens.text.primary, 0.04)}`,
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "14px",
    pointerEvents: "none",
    background: `linear-gradient(105deg, ${withAlpha(ctaAccentColor, 0.1)}, transparent 40%)`,
    opacity: 0,
    transition: "opacity .14s ease",
  },
  "&:hover": {
    transform: "translateY(-2px)",
    borderColor: withAlpha(colorTokens.text.primary, 0.18),
    boxShadow: `0 10px 28px ${withAlpha(colorTokens.bg.overlay, 0.38)}`,
  },
  "&:hover::after": {
    opacity: 1,
  },
  "&:focus-visible": {
    outline: `2px solid ${ctaAccentColor}`,
    boxShadow: `0 0 0 2px ${withAlpha(colorTokens.bg.overlay, 0.6)}`,
  },
};

export const menuTileBadgeRingSx: SxProps = {
  boxShadow: `0 0 0 2px ${withAlpha(colorTokens.bg.overlay, 0.6)}`,
};

export const menuPageShellSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box",
  height: "100dvh",
  maxHeight: "100dvh",
  overflow: "hidden",
  overflowX: "hidden",
  background: colorTokens.bg.app,
  textAlign: "center",
  padding: { xs: 1.5, sm: 2 },
};

/** Wrapper for `CompanyBrandBackdrop`; parent needs `position: "relative"`. */
export const moduleBrandBackdropLayerSx: SxProps = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  pointerEvents: "none",
  zIndex: 0,
  overflow: "hidden",
};

export type BrandBackdropModuleId =
  | "billing"
  | "communication"
  | "customers"
  | "events"
  | "trainings"
  | "inventory"
  | "stats"
  | "planning"
  | "hr"
  | "excursions"
  | "users"
  | "centre"
  | "rental";

export const brandBackdropModuleOpacity: Record<BrandBackdropModuleId, number> = {
  billing: 0.4,
  communication: 0.4,
  customers: 0.6,
  events: 0.4,
  trainings: 0.4,
  inventory: 0.4,
  stats: 0.4,
  planning: 0.4,
  hr: 0.4,
  excursions: 0.4,
  users: 0.4,
  centre: 0.4,
  rental: 0.4,
};

export const menuPageMainColumnSx: SxProps = {
  flex: "1 1 0",
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: { xs: 1.5, sm: 2 },
  paddingY: { xs: 1.5, sm: 1.5 },
  width: "100%",
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
};

export const menuHeaderContainerSx: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  maxWidth: "100%",
  mb: { xs: 1, sm: 2 },
};

export const menuSliderNavSx: SxProps = {
  display: { xs: "flex", sm: "none" },
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  maxWidth: "95vw",
  mx: "auto",
  px: 1,
  py: 1.25,
  gap: 1,
};

export const companyBrandLogoImgSizeSx: SxProps = {
  display: "block",
  maxHeight: { xs: 80, sm: 96, md: 112 },
  maxWidth: { xs: "min(92vw, 300px)", sm: "min(92vw, 380px)", md: 420 },
  width: "auto",
  height: "auto",
  objectFit: "contain",
};

/** Company `LogoUrl` on menu home: wide banners use maxWidth + maxHeight with contain. */
export const companyLogoMenuBackdropSizeSx: SxProps = {
  display: "block",
  maxHeight: { xs: 80, sm: 80, md: 88 },
  maxWidth: { xs: "min(92vw, 300px)", sm: 420, md: 460 },
  width: "auto",
  height: "auto",
  objectFit: "contain",
};

export function companyBrandBackdropImgSx(hasCompanyLogo: boolean, opacity: number): SxProps {
  return [
    hasCompanyLogo ? companyLogoMenuBackdropSizeSx : companyBrandLogoImgSizeSx,
    { opacity, userSelect: "none" },
  ] as SxProps;
}

/** Default Zekay static PNG on menu home: tighter box (often near-square). */
export const menuHeaderLogoImgSizeSx: SxProps = {
  display: "block",
  maxHeight: { xs: 56, sm: 64, md: 72 },
  maxWidth: { xs: "min(92vw, 220px)", sm: 260, md: 280 },
  width: "auto",
  height: "auto",
  objectFit: "contain",
};

/** Regroupement : 3 blocs, largeur min confortable pour libellés sur une ligne. */
export const menuPageCardGroupsGridSx: SxProps = {
  display: "grid",
  alignSelf: "stretch",
  width: "100%",
  minWidth: 0,
  maxWidth: { xs: "min(100%, 95vw)", sm: 1200, md: 1280, lg: 1320 },
  px: { xs: 0.75, sm: 1.5, md: 2.25 },
  mx: "auto",
  boxSizing: "border-box",
  alignItems: "stretch",
  gap: { xs: 1.75, sm: 2, md: 2.25 },
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(auto-fit, minmax(300px, 1fr))",
  },
};

export const menuCategoryPanelSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "flex-start",
  gap: 1.5,
  p: { xs: 1.5, sm: 1.75, md: 2 },
  minWidth: 0,
  width: "100%",
  minHeight: 0,
  height: "100%",
  boxSizing: "border-box",
  borderRadius: "16px",
  border: `1px solid ${withAlpha(colorTokens.text.primary, 0.1)}`,
  background: `linear-gradient(170deg, ${withAlpha(colorTokens.text.primary, 0.08)} 0%, ${withAlpha(colorTokens.text.primary, 0.02)} 100%)`,
  boxShadow: `0 1px 0 ${withAlpha(colorTokens.text.primary, 0.04)}`,
};

export const menuCategoryTitleRowSx: SxProps = {
  width: "100%",
  textAlign: "left",
  pb: 0.25,
  borderBottom: `1px solid ${withAlpha(colorTokens.text.primary, 0.1)}`,
  mb: 0.25,
};

/** Une seule colonne de tuiles pleine largeur de carte — évite les césures mots sur 2 colonnes étroites. */
export const menuCategoryInnerTilesGridSx: SxProps = {
  display: "grid",
  width: "100%",
  minWidth: 0,
  gridTemplateColumns: "1fr",
  gap: { xs: 1.1, sm: 1.25, md: 1.35 },
  alignContent: "start",
  flex: "0 0 auto",
};

export const menuCategoryTileCellSx: SxProps = {
  minWidth: 0,
  width: "100%",
  boxSizing: "border-box",
};

export const listRowDarkCardSx: SxProps = {
  width: "100%",
  mb: 1.5,
};

/**
 * Carte de liste en dégradé + hover (UserList, Customers, etc.) — `Box` racine, pas `DarkCard`.
 */
// Deep-link highlight: tint the training row background when it is the navigation target.
export const trainingRowHighlightBgSx = {
  background: withAlpha(ctaAccentColor, 0.18),
  "&:hover": { background: withAlpha(ctaAccentColor, 0.18) },
  transition: "background 0.4s ease",
} as const;

export const listRowAlertBg = withAlpha(colorTokens.status.danger, 0.14);
export const listRowAlertBgHover = withAlpha(colorTokens.status.danger, 0.2);
export const listRowAlertBorder = withAlpha(colorTokens.status.danger, 0.5);

/** Danger wash shared by equipment cards and idle training cards. */
export const listRowInactivityAlertSx = {
  background: listRowAlertBg,
  border: `1px solid ${listRowAlertBorder}`,
  "&:hover": {
    background: listRowAlertBgHover,
    borderColor: listRowAlertBorder,
  },
} as const;

export const gradientListRowCardShellSx: SxProps = {
  background: `linear-gradient(135deg, ${colorTokens.bg.surface} 0%, ${colorTokens.bg.surfaceMuted} 100%)`,
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "8px",
  transition: "all 0.2s ease-in-out",
  mb: 1.5,
  minWidth: 0,
  width: "100%",
  "&:hover": {
    background: `linear-gradient(135deg, ${colorTokens.bg.surfaceElevated} 0%, ${colorTokens.bg.surfaceMuted} 100%)`,
    borderColor: colorTokens.border.strong,
  },
};

/** Directory-only users (no login) — blue-tinted row to distinguish from full accounts. */
export const gradientListRowCardShellDirectorySx: SxProps = {
  background: `linear-gradient(135deg, ${withAlpha(colorTokens.status.info, 0.14)} 0%, ${withAlpha(colorTokens.status.info, 0.06)} 100%)`,
  border: `1px solid ${withAlpha(colorTokens.status.info, 0.35)}`,
  borderRadius: "8px",
  transition: "all 0.2s ease-in-out",
  mb: 1.5,
  minWidth: 0,
  width: "100%",
  "&:hover": {
    background: `linear-gradient(135deg, ${withAlpha(colorTokens.status.info, 0.22)} 0%, ${withAlpha(colorTokens.status.info, 0.1)} 100%)`,
    borderColor: withAlpha(colorTokens.status.info, 0.55),
  },
};

/**
 * `IconButtonCTA` `variant="plain"` dans l’en-tête d’une ligne (actions formations / sorties / profil…).
 * À combiner avec `...listRowIconButtonPlainSx, "&:hover": { ... }` si besoin d’un fond au survol ciblé.
 */
export const listRowIconButtonPlainSx: SxProps = {
  minHeight: "36px",
  minWidth: "36px",
  padding: "6px",
  "& svg, & .MuiSvgIcon-root": {
    fontSize: "1.35rem",
  },
};

export const listRowAccentPlainSx: SxProps = {
  ...listRowIconButtonPlainSx,
  "&:hover": { backgroundColor: withAlpha(colorTokens.accent.primary, 0.1) },
};

export const listRowDangerPlainSx: SxProps = {
  ...listRowIconButtonPlainSx,
  "&:hover": { backgroundColor: withAlpha(colorTokens.status.danger, 0.1) },
};

/** Stacked field label above value (RentalCard, UserItem, …). */
export const listRowFieldLabelSx: SxProps = {
  color: textTokens.color.subtle,
  fontWeight: 600,
  mb: 0.3,
  textTransform: "uppercase",
  letterSpacing: "0.3px",
  fontSize: "0.65rem",
};

/** Stacked label row with optional decorator icon. */
export const listRowFieldLabelWithIconSx: SxProps = {
  ...listRowFieldLabelSx,
  display: "flex",
  alignItems: "center",
  gap: 0.5,
};

export const listRowFieldLabelEmphasisWithIconSx: SxProps = {
  ...listRowFieldLabelWithIconSx,
  color: textTokens.color.title,
};

/** Details grid spanning card width (label above value per column). */
export const listRowDetailsGridSx: SxProps = {
  display: "grid",
  gridTemplateColumns: {
    xs: "repeat(2, minmax(0, 1fr))",
    sm: "repeat(auto-fit, minmax(0, 1fr))",
  },
  gap: 1,
  alignItems: "start",
  width: "100%",
  minWidth: 0,
};

export const listRowDetailItemStackedSx: SxProps = {
  minWidth: 0,
  width: "100%",
};

export const listRowDetailValueRowSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  flexWrap: "nowrap",
  minWidth: 0,
  width: "100%",
};

/** URL + actions grouped on the left (no space-between stretch). */
export const listRowPublicLinkValueRowSx: SxProps = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  flexWrap: "nowrap",
  maxWidth: "100%",
  minWidth: 0,
};

export const listRowPublicLinkTextSx: SxProps = {
  color: colorTokens.accent.primary,
  textDecoration: "none",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  flex: "0 1 auto",
  minWidth: 0,
  maxWidth: "100%",
  "&:hover": { textDecoration: "underline" },
};

/** Icon actions grouped beside an ellipsis link (EventCard public URL). */
export const listRowPublicLinkActionsSx: SxProps = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.25,
  flexShrink: 0,
};

export const listRowEllipsisLinkSx: SxProps = {
  color: colorTokens.accent.primary,
  textDecoration: "none",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  minWidth: 0,
  flex: 1,
  "&:hover": { textDecoration: "underline" },
};

export const listRowCompactIconButtonSx: SxProps = {
  minHeight: 20,
  minWidth: 20,
  p: 0.25,
  flexShrink: 0,
};

export const customerItemCardSx: SxProps = listRowDarkCardSx;

export const customerItemExpandAreaSx: SxProps = {
  borderTop: `1px solid ${colorTokens.border.default}`,
  mt: 1.5,
  pt: 1.5,
  animation: "slideDown 0.3s ease",
};

export const customerItemTypographySx: SxProps = {
  fontSize: "clamp(0.8rem, 2vw, 1rem)",
};

/** Shared shell for excursion card tooltip, planning day tooltip, etc. */
export const gradientTooltipCardShellSx: SxProps = {
  background: `linear-gradient(145deg, ${colorTokens.bg.surfaceMuted} 0%, ${colorTokens.bg.surfaceElevated} 52%, ${withAlpha(colorTokens.bg.surfaceMuted, 0.97)} 100%)`,
  border: `1px solid ${colorTokens.border.strong}`,
  borderRadius: "8px",
  p: 1.5,
  minWidth: { xs: "min(320px, 95vw)", sm: 400 },
  maxWidth: { xs: "95vw", sm: 600 },
  boxShadow: `0 8px 28px ${withAlpha("#000000", 0.24)}`,
};

/** Joy Tooltip root: no padding/border/shadow when `title` is a full custom surface (e.g. GradientTooltipCard). */
export const joyTooltipCustomContentHostSx: SxProps = {
  p: 0,
  border: "none",
  boxShadow: "none",
  backgroundColor: "transparent",
  backgroundImage: "none",
  "--variant-borderWidth": "0px",
};

/** RoleCard: permission label pills on dark surface. */
export const rolePermissionChipSx: SxProps = {
  px: 1,
  py: 0.5,
  borderRadius: 6,
  background: colorTokens.bg.surfaceMuted,
  border: `1px solid ${colorTokens.border.default}`,
  fontSize: "0.82rem",
  color: colorTokens.text.secondary,
  fontWeight: textTokens.weight.semibold,
};

/** RoleCard + PermissionsPicker: module / section title above permission rows. */
export const rolePermissionModuleHeadingSx: SxProps = {
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  mb: 0.75,
};

export const rolePermissionPreviewSectionsColumnSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: 1.5,
};

export const rolePermissionChipsRowSx: SxProps = {
  display: "flex",
  gap: 1,
  flexWrap: "wrap",
};

export const roleCardTitleSx: SxProps = { mb: 1 };

export const roleCardFooterActionsSx: SxProps = {
  display: "flex",
  gap: 1,
  mt: 1,
};

/** PermissionsPicker: stacked module groups inside the sheet. */
export const permissionsPickerModuleGroupsSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

export const permissionsPickerSheetCompactSx: SxProps = {
  p: 1.5,
  borderRadius: 8,
  maxHeight: 360,
  overflow: "auto",
  backgroundColor: colorTokens.bg.app,
};

export const permissionsPickerSheetFillSx: SxProps = {
  p: 1.5,
  borderRadius: 8,
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  backgroundColor: colorTokens.bg.app,
};

export const permissionsPickerPanelTitleSx: SxProps = {
  mb: 0.75,
  flexShrink: 0,
  color: colorTokens.accent.primary,
};

export const statsBadgeSx: SxProps = {
  padding: "2px 4px",
  borderRadius: "12px",
  fontWeight: 500,
  fontSize: "inherit",
};

export const statsSectionAccordionSx: SxProps = {
  mb: { xs: 2, md: 2.5 },
  minWidth: 0,
  backgroundColor: "transparent",
  borderBottom: "none",
  "& > .MuiAccordionSummary-root .MuiAccordionSummary-button": {
    minHeight: 44,
    px: { xs: 0.5, md: 1 },
    borderRadius: "8px",
    color: colorTokens.text.title,
    "&:hover, &:not(.Mui-selected):hover": {
      backgroundColor: colorTokens.bg.hover,
      color: colorTokens.text.title,
    },
  },
  "& .MuiAccordionSummary-indicator": {
    color: colorTokens.text.title,
  },
  "& > .MuiAccordionDetails-root .MuiAccordionDetails-content": {
    px: 0,
    pt: 1,
  },
};

export const statsStatRowSx: SxProps = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const statsBadgeSuccessSx: SxProps = {
  ...statsBadgeSx,
  backgroundColor: colorTokens.status.success,
  color: colorTokens.text.primary,
};

export const statsBadgeDangerSx: SxProps = {
  ...statsBadgeSx,
  backgroundColor: colorTokens.status.danger,
  color: colorTokens.text.primary,
};

export const statsBadgeInfoSx: SxProps = {
  ...statsBadgeSx,
  backgroundColor: colorTokens.accent.primary,
  color: colorTokens.text.primary,
};

export const statsBadgeWarningSx: SxProps = {
  ...statsBadgeSx,
  backgroundColor: colorTokens.status.warning,
  color: colorTokens.text.inverse,
};

export const statsBadgeMutedSx: SxProps = {
  ...statsBadgeSx,
  backgroundColor: colorTokens.bg.hover,
  color: textTokens.color.primary,
};

export const statsPeriodNavButtonSx: SxProps = {
  minHeight: 36,
  minWidth: 36,
  flexShrink: 0,
};

export const statsPeriodLabelSx: SxProps = {
  flex: "0 1 auto",
  minWidth: 0,
  maxWidth: { xs: 180, sm: 280 },
  textAlign: "center",
  fontSize: { xs: "0.9rem", sm: "1rem" },
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  px: 0.5,
};

export const statsYearSelectorButtonSelectedSx: SxProps = {
  minWidth: { xs: 56, sm: 64 },
  bgcolor: `${ctaAccentColor} !important`,
  color: `${colorTokens.text.inverse} !important`,
  borderColor: `${ctaAccentColor} !important`,
  "&:hover": {
    bgcolor: `${ctaAccentHoverColor} !important`,
    borderColor: `${ctaAccentHoverColor} !important`,
  },
};

export const statsYearSelectorButtonOutlinedSx: SxProps = {
  minWidth: { xs: 56, sm: 64 },
  borderColor: colorTokens.border.strong,
  color: textTokens.color.secondary,
  "&:hover": {
    bgcolor: withAlpha(colorTokens.text.primary, 0.08),
    borderColor: colorTokens.border.focus,
  },
};

export const darkDividerBorder = colorTokens.border.default;

/** Horizontal top bar (dashboard NavBar) */
export const navBarBottomBorderSx: SxProps = {
  borderBottom: `1px solid ${colorTokens.border.default}`,
};

/** Vertical rule between logo block and nav items (NavBar) */
export const navVerticalDividerSx: SxProps = {
  height: "90%",
  alignSelf: "center",
  bgcolor: colorTokens.border.default,
};

/** Wordmark next to home — monospace; 15px chrome. */
export const navBrandWordmarkSx: SxProps = {
  cursor: "pointer",
  fontFamily: codeFontFamily,
  fontSize: "15px",
  letterSpacing: "0.08em",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** Login / reset cards (`background.body` = app): autofill matches card, no white slab. Includes vertical spacing. */
export const authCardDarkInputSx: SxProps = {
  marginY: 1,
  "& input:-webkit-autofill": {
    WebkitTextFillColor: `${colorTokens.text.primary} !important`,
    caretColor: colorTokens.text.primary,
    boxShadow: `0 0 0 1000px ${colorTokens.bg.app} inset`,
  },
  "& input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
    boxShadow: `0 0 0 1000px ${colorTokens.bg.app} inset`,
  },
};

// ─── components/common (shared chrome) ───────────────────────────────────────

/** DarkInput default: underline field on dark surface. */
export const darkInputUnderlineRootSx: SxProps = {
  backgroundColor: "transparent",
  color: colorTokens.text.primary,
  "--Input-gap": "0px",
  borderRadius: 0,
  border: "none",
  borderBottom: `1px solid ${colorTokens.border.strong}`,
  boxShadow: "none",
  py: "6px",
  px: "2px",
  minHeight: "2.25rem",
  "&::before": {
    transform: "none",
    border: "none",
    boxShadow: "none",
    backgroundImage: "none",
  },
  "&::after": { display: "none" },
  "&:hover": {
    backgroundColor: "transparent",
    borderBottomColor: colorTokens.border.focus,
  },
  "&:focus-within": {
    backgroundColor: "transparent",
    borderBottomColor: colorTokens.accent.primary,
    outline: "none",
  },
  "& input": {
    color: colorTokens.text.primary,
    backgroundColor: "transparent",
  },
  "& input::placeholder": {
    color: colorTokens.text.muted,
    opacity: 1,
  },
  "& .MuiInput-endDecorator, & .MuiInput-startDecorator": {
    backgroundColor: "transparent",
  },
  "& input:-webkit-autofill": {
    WebkitTextFillColor: `${colorTokens.text.primary} !important`,
    caretColor: colorTokens.text.primary,
    boxShadow: `0 0 0 1000px ${colorTokens.bg.surface} inset`,
  },
  "& input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
    boxShadow: `0 0 0 1000px ${colorTokens.bg.surface} inset`,
  },
};

/** SearchBar: extra horizontal inset and icon gap (underline DarkInput defaults are very tight). */
export const searchBarFieldSx: SxProps = {
  width: "100%",
  px: { xs: 1.5, sm: 1.75 },
  "--Input-gap": "10px",
  backgroundColor: colorTokens.bg.surfaceMuted,
  "& input": {
    color: colorTokens.text.primary,
  },
  "& input::placeholder": {
    color: colorTokens.text.muted,
  },
};

export type CommonModalSize = "sm" | "md" | "lg" | "xl";

export const commonModalSizeDimensions: Record<
  CommonModalSize,
  { width: Record<string, string>; height: Record<string, string> }
> = {
  sm: { width: { xs: "95vw", sm: "50vw" }, height: { xs: "80dvh", sm: "50vh" } },
  md: { width: { xs: "95vw", sm: "65vw" }, height: { xs: "85dvh", sm: "65vh" } },
  lg: { width: { xs: "95vw", sm: "80vw" }, height: { xs: "90dvh", sm: "80vh" } },
  xl: { width: { xs: "95vw", sm: "95vw" }, height: { xs: "95dvh", sm: "95vh" } },
};

export const commonModalBackdropSx: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

export const commonModalDialogBaseSx: SxProps = {
  backgroundColor: colorTokens.bg.surface,
  color: colorTokens.text.primary,
  overflow: "visible",
  mx: "auto",
  my: { xs: 1, sm: 2 },
  display: "flex",
  flexDirection: "column",
};

export const commonModalDialogRadiusSx: SxProps = { borderRadius: { xs: "8px", sm: "12px" } };

export function commonModalDialogSizeSx(size: CommonModalSize, fitContentHeight: boolean): SxProps {
  const dimensions = commonModalSizeDimensions[size];

  if (fitContentHeight) {
    return {
      width: dimensions.width,
      maxWidth: dimensions.width,
      height: "auto",
      maxHeight: dimensions.height,
      minHeight: "auto",
    };
  }

  return {
    width: dimensions.width,
    maxWidth: dimensions.width,
    height: dimensions.height,
    maxHeight: dimensions.height,
  };
}

export const commonModalHeaderRowSx: SxProps = {
  flexShrink: 0,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: { xs: 0.75, sm: 1 },
  mb: { xs: 1.25, sm: 2 },
};

export const commonModalTitleWrapSx: SxProps = { minWidth: 0, flex: 1, pr: 0.5 };

export const commonModalTitleTypographySx: SxProps = {
  fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" },
  lineHeight: 1.25,
  wordBreak: "break-word",
};

export const commonModalSubtitleTypographySx: SxProps = {
  mt: 0.75,
  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
  lineHeight: 1.4,
  wordBreak: "break-word",
};

export const commonModalCloseButtonSx: SxProps = {
  color: colorTokens.accent.primary,
  "&:hover": { backgroundColor: colorTokens.bg.surfaceElevated },
};

export function commonModalBodySx(fitContentHeight: boolean): SxProps {
  return {
    flex: fitContentHeight ? "0 1 auto" : 1,
    minHeight: fitContentHeight ? "auto" : 0,
    maxHeight: fitContentHeight ? "min(70dvh, calc(100dvh - 9rem))" : undefined,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: { xs: 1.5, sm: 2 },
    pr: { xs: 0.5, sm: 1 },
    pb: 1,
    backgroundColor: colorTokens.bg.surface,
    overscrollBehavior: "contain",
    scrollbarColor: `${colorTokens.border.strong} ${colorTokens.bg.surface}`,
  };
}

export function commonModalFooterRowSx(showDefaultSave: boolean): SxProps {
  return {
    flexShrink: 0,
    display: "flex",
    gap: { xs: 1, sm: 2 },
    alignItems: "center",
    justifyContent: showDefaultSave ? "space-between" : "flex-start",
    flexWrap: "wrap",
    mt: 2,
    pt: 2,
    borderTop: "1px solid",
    borderColor: "divider",
  };
}

export const commonModalFooterActionsFlexSx: SxProps = {
  display: "flex",
  gap: 1,
  alignItems: "center",
};

export const commonModalFooterSaveWrapSx: SxProps = {
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
};

export const commonModalConfirmIconButtonSx: SxProps = {
  "&:disabled": {
    backgroundColor: colorTokens.bg.surfaceMuted,
    color: colorTokens.text.muted,
  },
};

export const accountBoxRootSx: SxProps = {
  display: "flex",
  alignItems: "center",
  ml: "auto",
  gap: 1,
};

export const accountBoxUsernameTypographySx: SxProps = {
  display: { xs: "none", md: "block" },
  fontSize: "15px",
};

export const navBarSheetSx: SxProps = {
  width: "100%",
  height: { xs: "auto", md: "7vh" },
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  p: 1,
  ...navBarBottomBorderSx,
  backgroundColor: colorTokens.bg.app,
  position: "relative",
  overflow: "visible",
};

/** Inner row inside the NavBar Sheet — keeps the icons row layout on every breakpoint. */
export const navBarTopRowSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 2,
  width: "100%",
  minHeight: "calc(7vh - 16px)",
  position: "relative",
};

/** Mobile-only second row showing the active page title under the icons. */
export const navBarMobileTitleRowSx: SxProps = {
  display: { xs: "flex", md: "none" },
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  pt: 0.75,
  mt: 0.75,
  borderTop: `1px solid ${colorTokens.border.default}`,
};

export const navBarMobileTitleTypographySx: SxProps = {
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: 1.25,
  textAlign: "center",
};

export const navBarLogoClusterSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: { xs: "auto", md: "125px" },
  overflow: "visible",
  pl: 0.5,
};

export const navBarListSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  gap: 1,
  color: colorTokens.text.primary,
  flexGrow: 1,
  alignItems: "center",
  overflowX: "auto",
  pl: 1,
  "&::-webkit-scrollbar": { display: "none" },
};

export const navBarListItemSx: SxProps = { p: 0, overflow: "visible" };

export const navBarButtonSx: SxProps = {
  "&:hover": {
    backgroundColor: `${withAlpha(colorTokens.accent.primary, 0.2)} !important`,
  },
  justifyContent: "center",
};

export const navBarIconDecoratorSx: SxProps = {
  minWidth: "auto",
  justifyContent: "center",
  mx: 0.5,
};

export const navBarPageTitleAnchorSx: SxProps = {
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%,-50%)",
  pointerEvents: "none",
  display: { xs: "none", md: "block" },
};

export const navBarPageTitleTypographySx: SxProps = {
  fontWeight: 600,
  fontSize: "15px",
  lineHeight: 1.25,
};

export const navBarRightClusterSx: SxProps = { display: "flex", alignItems: "center", gap: 1 };

export const footerBrandColumnSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.5,
};

export const footerBrandRowCenteredSx: SxProps = { display: "flex", alignItems: "center", gap: 1 };

export const footerBrandMetaRowSx: SxProps = { display: "flex", alignItems: "center", gap: 0.75 };

export const footerBrandCaption12Sx: SxProps = { fontSize: 12 };

export const footerBrandCaption11Sx: SxProps = { fontSize: 11 };

export const footerBrandCaption11ItalicSx: SxProps = { fontSize: 11, fontStyle: "italic" };

export const footerBrandCaptionSeparatorSx: SxProps = { fontSize: 11, opacity: 0.7 };

export const tabsPageRootColumnSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  flex: 1,
};

export const tabsPageInnerGrowSx: SxProps = {
  display: "flex",
  flexGrow: 1,
  minHeight: 0,
  backgroundColor: "transparent",
};

export const tabsPageTabsRootSx: SxProps = {
  display: "flex",
  flexGrow: 1,
  minHeight: 0,
  width: "100%",
  backgroundColor: "transparent",
};

export const tabsPageTabListVerticalSx: SxProps = {
  width: { xs: 158, sm: 174 },
  borderRight: `1px solid ${colorTokens.border.default}`,
  backgroundColor: colorTokens.bg.surface,
  flexShrink: 0,
};

export const tabsPageTabSx: SxProps = {
  color: colorTokens.text.primary,
  py: { xs: 1, sm: 0.875 },
  minHeight: { xs: 48, sm: 44 },
  "&:hover": { backgroundColor: colorTokens.bg.hover },
  "&.Mui-selected": {
    color: colorTokens.accent.contrastText,
    backgroundColor: colorTokens.accent.primary,
    fontWeight: 700,
  },
};

export const tabsPageTabLabelInheritSx: SxProps = {
  color: "inherit",
  fontWeight: "inherit",
  fontSize: "15px",
  lineHeight: 1.35,
};

export const tabsPageTabPanelSx: SxProps = {
  flexGrow: 1,
  minHeight: 0,
  overflow: "auto",
  p: 2,
  backgroundColor: "transparent",
};

export const instructorEncadrantFilterWrapperSx: SxProps = {
  flex: { sm: "1 1 220px" },
  maxWidth: { xs: "95vw", sm: 360 },
  minWidth: 0,
};

export const instructorEncadrantFilterLabelSx: SxProps = { mb: { xs: 0.25, sm: 0.5 } };

export const instructorEncadrantFilterSelectFullWidthSx: SxProps = { width: "100%" };

export const scrollableListPageLayoutHeaderStripSx: SxProps = { flexShrink: 0, width: "100%" };

export const scrollableListPageLayoutLoadingOverlaySx: SxProps = {
  position: "absolute",
  inset: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: withAlpha(colorTokens.bg.overlay, 0.4),
};

export const scrollableListPageLayoutPaginationStripSx: SxProps = {
  flexShrink: 0,
  width: "100%",
  zIndex: 5,
  minWidth: 0,
};

// ─── User list documents (`UserDocumentsSection`) ──────────────────────────────

const USER_DOCUMENTS_CTRL_H = 36;

export const userDocumentsSectionRootSx: SxProps = {
  py: 1,
  maxWidth: "100%",
  overflowX: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: 0,
  minHeight: 0,
};

export const userDocumentsAddHeadingRowSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mb: 1,
  minWidth: 0,
  maxWidth: "100%",
};

export const userDocumentsAddTitleTypographySx: SxProps = {
  fontWeight: 600,
  my: 0,
  lineHeight: 1.35,
};

export const userDocumentsUploadTriggerClusterSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  flexShrink: 0,
  minWidth: 0,
};

export const userDocumentsUploadTriggerLabelSx: SxProps = {
  fontWeight: 600,
  my: 0,
  lineHeight: 1.35,
  whiteSpace: "nowrap",
};

export const userDocumentsUploadRowShellSx: SxProps = {
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "8px",
  p: 1.5,
  mb: 1.5,
  backgroundColor: colorTokens.bg.surface,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: 1,
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

/** Expiry + warn fields on one row; each side is label + control for horizontal alignment. */
export const userDocumentsUploadExpiryAlertRowSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "nowrap",
  gap: 1,
  width: "100%",
  minWidth: 0,
};

export const userDocumentsListItemRowSx: SxProps = {
  border: `1px solid ${colorTokens.border.default}`,
  borderRadius: "8px",
  px: 1,
  py: 0.5,
  minHeight: USER_DOCUMENTS_CTRL_H + 8,
  backgroundColor: colorTokens.bg.surface,
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "center" },
  gap: 1,
  maxWidth: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

export const userDocumentsUniformControlSx: SxProps = {
  minHeight: USER_DOCUMENTS_CTRL_H,
  maxHeight: USER_DOCUMENTS_CTRL_H,
  "& .MuiInput-root": { minHeight: USER_DOCUMENTS_CTRL_H, maxHeight: USER_DOCUMENTS_CTRL_H },
  "& input": { minHeight: USER_DOCUMENTS_CTRL_H - 4, py: 0.5, boxSizing: "border-box" },
};

export const userDocumentsIconSlotSx: SxProps = {
  width: USER_DOCUMENTS_CTRL_H,
  height: USER_DOCUMENTS_CTRL_H,
  minWidth: USER_DOCUMENTS_CTRL_H,
  minHeight: USER_DOCUMENTS_CTRL_H,
  flexShrink: 0,
};

export const userDocumentsUploadNameFieldSx: SxProps = {
  ...userDocumentsUniformControlSx,
  width: "100%",
  minWidth: 0,
};

export const userDocumentsExpiryFieldClusterSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 0.75,
  minWidth: 0,
  width: { xs: "100%", sm: "auto" },
  flex: { sm: "0 0 auto" },
  alignSelf: { xs: "stretch", sm: "center" },
};

export const userDocumentsUploadExpiryClusterSx: SxProps = {
  ...userDocumentsExpiryFieldClusterSx,
  flex: "1 1 0%",
  minWidth: 0,
  width: "auto",
  alignSelf: "center",
};

export const userDocumentsUploadWarnClusterSx: SxProps = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 0.75,
  flexShrink: 0,
  minWidth: 0,
};

export const userDocumentsExpiryFieldLabelSx: SxProps = {
  flexShrink: 0,
  m: 0,
  whiteSpace: { xs: "normal", sm: "nowrap" },
  lineHeight: 1.15,
  display: "flex",
  alignItems: "center",
  minHeight: USER_DOCUMENTS_CTRL_H,
  alignSelf: "center",
};

export const userDocumentsExpiryDateFieldSx: SxProps = {
  ...userDocumentsUniformControlSx,
  flex: "1 1 auto",
  minWidth: 0,
  width: { xs: "100%", sm: 148 },
  maxWidth: { xs: "100%", sm: 148 },
};

export const userDocumentsWarnDaysFieldSx: SxProps = {
  ...userDocumentsUniformControlSx,
  width: { xs: "100%", sm: 88 },
  flex: { sm: "0 0 auto" },
  alignSelf: { xs: "stretch", sm: "center" },
};

export const userDocumentsUploadWarnDaysFieldSx: SxProps = {
  ...userDocumentsUniformControlSx,
  width: { xs: 72, sm: 76 },
  flex: "0 0 auto",
  minWidth: { xs: 64, sm: 72 },
};

export const userDocumentsEditNameFieldSx: SxProps = {
  ...userDocumentsUniformControlSx,
  flex: { sm: "1 1 72px" },
  minWidth: 0,
  width: { xs: "100%", sm: "auto" },
};

export const userDocumentsUploadTriggerWrapSx: SxProps = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  flexShrink: 0,
};

export const userDocumentsUploadProgressSx: SxProps = {
  "--CircularProgress-size": "22px",
  color: colorTokens.text.primary,
};

export const userDocumentsUploadButtonIconSx: SxProps = {
  color: colorTokens.accent.primary,
};

export const userDocumentsScrollAreaSx: SxProps = {
  maxHeight: { xs: "min(50vh, 320px)", sm: 400 },
  overflowY: "auto",
  pr: 0.5,
  flex: "1 1 auto",
  minHeight: 0,
};

export const userDocumentsDocumentListSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

export const userDocumentsEditActionsRowSx: SxProps = {
  display: "flex",
  gap: 0.5,
  flexShrink: 0,
  ml: { sm: "auto" },
  justifyContent: { xs: "flex-end", sm: "flex-start" },
  width: { xs: "100%", sm: "auto" },
  alignSelf: { xs: "stretch", sm: "center" },
};

export const userDocumentsEditCheckIconSx: SxProps = {
  color: colorTokens.status.success,
};

export const userDocumentsRowNameTypographySx: SxProps = {
  fontWeight: 600,
  flex: { sm: "1 1 auto" },
  minWidth: 0,
  width: { xs: "100%", sm: "auto" },
  overflow: { sm: "hidden" },
  textOverflow: { sm: "ellipsis" },
  whiteSpace: { xs: "normal", sm: "nowrap" },
  wordBreak: { xs: "break-word" },
  my: 0,
};

export const userDocumentsRowMetaRibbonSx: SxProps = {
  display: { xs: "flex", sm: "contents" },
  flexDirection: { xs: "row" },
  alignItems: "center",
  justifyContent: { xs: "space-between" },
  gap: { xs: 1 },
  width: { xs: "100%", sm: "auto" },
  minWidth: 0,
};

export const userDocumentsRowExpiryClusterSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  flexShrink: 0,
  whiteSpace: "nowrap",
};

export const userDocumentsExpiryMutedLineSx: SxProps = { my: 0 };

/** Warning glyph next to expiry line (colour encodes urgency). */
export function userDocumentsExpiryWarnIconSx(level: "expired" | "soon"): SxProps {
  return {
    flexShrink: 0,
    fontSize: 18,
    color: level === "expired" ? colorTokens.status.danger : colorTokens.status.warning,
  };
}

export const userDocumentsRowActionsClusterSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 0.25,
  flexShrink: 0,
};

export const userDocumentsViewActionIconSx: SxProps = {
  color: colorTokens.text.primary,
};

export const userDocumentsEditActionIconSx: SxProps = {
  color: colorTokens.accent.primary,
};

export const userDocumentsPaginationStripSx: SxProps = {
  flexShrink: 0,
  width: "100%",
  maxWidth: "100%",
  mt: 0.5,
};

export const cardValidationListRootSx: SxProps = {
  width: "100%",
  p: 2,
  pb: 0,
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};

export const cardValidationScrollAreaSx: SxProps = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  paddingRight: 1,
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    borderRadius: "4px",
  },
};

export const scrollableListPageLayoutScrollBoxSx: SxProps = {
  ...cardValidationScrollAreaSx,
  position: "relative",
};

export const cardValidationTableSx: SxProps = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: "0 15px",
  "& td": {
    border: "none",
    verticalAlign: "middle",
  },
};

export const cardValidationHeaderCellSx: SxProps = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  textAlign: "left",
  pb: "10px",
  borderBottom: "1px solid",
  borderColor: "divider",
  backgroundColor: "black",
};

export const cardValidationDividerRowSx: SxProps = {
  p: 0,
  border: "none",
};

/** Cartes validation row: wide identity track so qualification stays one line when space allows; chips column stays `1fr`. */
export const cardValidationItemRowSx: SxProps = {
  display: "grid",
  alignItems: "center",
  columnGap: 1,
  rowGap: 1,
  width: "100%",
  minWidth: 0,
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: "minmax(0, clamp(200px, 48vw, 520px)) minmax(0, 1fr) auto",
  },
};

/** Identity fills grid column 1; inner text uses ellipsis only past column width. */
export const cardValidationItemIdentityColumnSx: SxProps = {
  minWidth: 0,
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
};

/** Chips column: left-aligned horizontally; vertically centred in the row vs identity / CTAs. */
export const cardValidationItemCardsColumnSx: SxProps = {
  minWidth: 0,
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  px: 0.5,
};

/** Wrapper: centers chips on xs, left-aligned on sm+. */
export const cardValidationItemCardsCenterOuterSx: SxProps = {
  display: "flex",
  justifyContent: { xs: "center", sm: "flex-start" },
  alignItems: "center",
  width: "100%",
  minWidth: 0,
  py: { xs: 1, sm: 0 },
};

/** Chip cluster: column on xs (one chip per line, centered), inline row on sm+. */
export const cardValidationItemCardsChipClusterSx: SxProps = {
  display: { xs: "flex", sm: "inline-flex" },
  flexDirection: { xs: "column", sm: "row" },
  flexWrap: { sm: "wrap" },
  alignItems: "center",
  alignContent: "flex-start",
  justifyContent: { xs: "center", sm: "flex-start" },
  gap: { xs: 0.75, sm: 0.5 },
  maxWidth: "100%",
};

/** Comment / docs / status icons: single 8-col row on xs, inline flex on md+. */
export const cardValidationItemActionsClusterSx: SxProps = {
  display: { xs: "grid", md: "flex" },
  gridTemplateColumns: { xs: "repeat(8, minmax(0, 1fr))", md: "none" },
  flexWrap: { md: "wrap" },
  alignItems: "center",
  justifyItems: { xs: "center", md: "stretch" },
  justifyContent: { md: "flex-start" },
  gap: { xs: 0.5, md: 1 },
  rowGap: 1,
  flexShrink: 0,
  width: { xs: "100%", md: "auto" },
  justifySelf: { xs: "stretch", md: "auto" },
};

export const darkScrollAreaSx: SxProps = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    borderRadius: "4px",
  },
};

/** Dark dropdown list for search results / autocomplete (readable text, hover). */
export const darkDropdownListSx: SxProps = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 10,
  maxHeight: 200,
  overflowY: "auto",
  backgroundColor: "#333",
  border: "1px solid #555",
  borderRadius: 8,
  mt: 0.25,
  p: 0,
  "& .MuiListItemButton-root": {
    color: textTokens.color.primary,
    "&:hover": { backgroundColor: "#444" },
  },
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { backgroundColor: "rgba(255, 255, 255, 0.1)" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255, 255, 255, 0.3)",
    borderRadius: "4px",
  },
};

export const darkDividerLineSx: SxProps = {
  height: "1px",
  bgcolor: "rgba(255, 255, 255, 0.1)",
  width: "100%",
};

export const listTableWrapperSx: SxProps = {
  flexGrow: 1,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

export const loadingCenterBoxSx: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
};

export const stickyTableHeaderCellSx: SxProps = {
  textAlign: "left",
  color: textTokens.color.primary,
  width: `${100 / 13}%`,
  position: "sticky",
  top: 0,
  zIndex: 2,
  wordWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
  paddingBottom: "10px",
};

export const headerRowSx: SxProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};

export const settingsScrollPageSx: SxProps = {
  width: "100%",
  padding: 2,
  overflowY: "auto",
  height: "100%",
  maxHeight: "100%",
};

export const settingsScrollPageStackSx: SxProps = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
};

export const headerLeftGroupSx: SxProps = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

/** Qualification / template `DarkSelect` in trainings list header and template editors. */
export const trainingsQualificationSelectSx: SxProps = {
  minWidth: { xs: "100%", sm: 300 },
  maxWidth: "100%",
  boxSizing: "border-box",
};

/** Status `DarkSelect` in trainings list header (aligned with qualification select). */
export const trainingsStatusSelectSx: SxProps = {
  minWidth: { xs: "100%", sm: 200 },
  maxWidth: "100%",
  boxSizing: "border-box",
};

/** Compact badge for icon buttons in training/card-validation list rows. */
export const trainingRowBadgeSx: SxProps = {
  "& .MuiBadge-badge": {
    fontSize: "0.625rem",
    minWidth: 14,
    minHeight: 14,
    height: 14,
    padding: "0 4px",
    lineHeight: 1,
  },
};

/** Grid row for a training item (xs: single column stack; md+: identity | actions). */
export const trainingItemRowSx: SxProps = {
  display: "grid",
  alignItems: "center",
  columnGap: 1,
  rowGap: 1,
  width: "100%",
  minWidth: 0,
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    md: "minmax(0, 1fr) auto",
  },
};

/** Identity + sessions cluster: stacks vertically on xs, inline on md+. */
export const trainingItemIdentityColumnSx: SxProps = {
  minWidth: 0,
  width: "100%",
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  alignItems: { xs: "stretch", md: "center" },
  gap: 1,
};

/** Action icons cluster: single 8-col row on xs, inline flex on md+. */
export const trainingItemActionsClusterSx: SxProps = {
  display: { xs: "grid", md: "flex" },
  gridTemplateColumns: { xs: "repeat(8, minmax(0, 1fr))", md: "none" },
  flexWrap: { md: "wrap" },
  alignItems: "center",
  justifyItems: { xs: "center", md: "stretch" },
  justifyContent: { md: "flex-end" },
  gap: { xs: 0.5, md: 1 },
  rowGap: 1,
  flexShrink: 0,
  width: { xs: "100%", md: "auto" },
};

/** Sessions input wrapper: centered on xs with vertical air, inline on md+. */
export const trainingItemSessionsWrapperSx: SxProps = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 0.25,
  mx: { xs: 0, md: 0.5 },
  py: { xs: 1, md: 0 },
  flexShrink: 0,
  alignSelf: { xs: "center", md: "auto" },
};

/** Main scroll area under trainings template editors header (aligned with list editors). */
export const trainingsTemplateDashboardLayoutSx: SxProps = {
  flex: 1,
  minHeight: 0,
  px: 2,
  pt: 1,
  pb: 4,
};

/** Fixed bottom-right bar for add/save in course and QCM template editors. */
export const trainingsEditorDockedActionsBarSx: SxProps = {
  position: "fixed",
  bottom: 0,
  right: 0,
  width: "10%",
  height: "60px",
  backgroundColor: colorTokens.bg.surface,
  zIndex: 1000,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "10px 0 0 0",
};

export const trainingItemRowCellSx: SxProps = {
  textAlign: "left",
  paddingTop: "10px",
  paddingBottom: "10px",
  width: `${100 / 13}%`,
  wordWrap: "break-word",
  wordBreak: "break-word",
  whiteSpace: "normal",
};

export const dayCellAddBtnSx: SxProps = {
  "--IconButton-size": "22px",
  minWidth: 22,
  minHeight: 22,
  borderRadius: "50%",
  p: 0,
  color: colorTokens.text.title,
  opacity: 0.7,
  "&:hover": { opacity: 1, backgroundColor: withAlpha(colorTokens.text.title, 0.12) },
};

export const dayCellMenuSx: SxProps = {
  minWidth: 160,
  zIndex: 1400,
  backgroundColor: colorTokens.bg.surfaceElevated,
  border: `1px solid ${colorTokens.border.strong}`,
  borderRadius: "10px",
  boxShadow: `0 8px 24px ${withAlpha("#000000", 0.35)}`,
  py: 0.5,
  "& .MuiMenuItem-root": {
    color: colorTokens.text.primary,
    fontWeight: 500,
    fontSize: "0.85rem",
    borderRadius: "6px",
    mx: 0.5,
    "&:hover": {
      backgroundColor: colorTokens.bg.hover,
    },
  },
};
